import { ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from '@cloudflare/kumo/components/sidebar';
import { Button } from '@cloudflare/kumo/components/button';
import {
  House,
  Users,
  Storefront,
  ClipboardText,
  EnvelopeSimple,
  EnvelopeOpen,
  Megaphone,
  CurrencyJpy,
  Heart,
  MapTrifold,
  Gear,
  SignOut,
  UsersThree,
  UserPlus,
  List,
  IdentificationCard,
} from '@phosphor-icons/react';
import { authClient, useSession } from '@/lib/authClient';

interface NavItem {
  to: string;
  label: string;
  icon: typeof House;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'ダッシュボード', icon: House },
  { to: '/exhibitors', label: '出展者', icon: Storefront },
  { to: '/content-review', label: '内容確認', icon: ClipboardText },
  { to: '/adjacency-groups', label: '隣接配置希望', icon: UsersThree },
  { to: '/users', label: 'ユーザー', icon: Users },
  { to: '/emails', label: 'メール送信', icon: EnvelopeSimple },
  { to: '/broadcast', label: 'メルマガ・お知らせ', icon: Megaphone },
  { to: '/email-templates', label: 'メールテンプレ', icon: EnvelopeOpen },
  { to: '/payments', label: '支払い管理', icon: CurrencyJpy },
  { to: '/sponsorships', label: '少額スポンサー', icon: Heart },
  { to: '/booths', label: 'ブース割当', icon: MapTrifold },
  { to: '/checkins', label: '当日受付', icon: IdentificationCard },
  { to: '/application-allowlist', label: '申込許可リスト', icon: UserPlus },
  { to: '/settings', label: '設定', icon: Gear },
];

interface Props {
  children: ReactNode;
}

export default function AppShell({ children }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useSession();

  return (
    <Sidebar.Provider defaultOpen>
      <div className="flex min-h-screen w-full bg-kumo-base">
        <Sidebar>
          <Sidebar.Header>
            <span className="text-base font-semibold text-kumo-default">NT東京 管理</span>
          </Sidebar.Header>
          <Sidebar.Content>
            <Sidebar.Group>
              <Sidebar.GroupLabel>メニュー</Sidebar.GroupLabel>
              <Sidebar.GroupContent>
                <Sidebar.Menu>
                  {NAV_ITEMS.map((item) => {
                    const active =
                      item.to === '/'
                        ? location.pathname === '/'
                        : location.pathname.startsWith(item.to);
                    return (
                      <NavLink key={item.to} to={item.to} className="contents">
                        <Sidebar.MenuButton icon={item.icon} active={active}>
                          {item.label}
                        </Sidebar.MenuButton>
                      </NavLink>
                    );
                  })}
                </Sidebar.Menu>
              </Sidebar.GroupContent>
            </Sidebar.Group>
          </Sidebar.Content>
          <Sidebar.Footer>
            {data?.user ? (
              <div className="flex flex-col gap-2 px-2 py-2">
                <span className="truncate text-xs text-kumo-muted">{data.user.email}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={SignOut}
                  onClick={async () => {
                    await authClient.signOut();
                    navigate('/sign-in');
                  }}
                >
                  ログアウト
                </Button>
              </div>
            ) : null}
            <Sidebar.Trigger />
          </Sidebar.Footer>
        </Sidebar>
        <div className="flex min-w-0 flex-1 flex-col overflow-x-hidden">
          {/* モバイル用ヘッダー: サイドバーが Dialog になり画面から到達不能になるため、
              開閉トリガーをサイドバー外側にも配置する */}
          <div className="flex items-center gap-2 border-b border-kumo-border p-3 md:hidden">
            <Sidebar.Trigger aria-label="メニューを開く">
              <List className="size-5" />
            </Sidebar.Trigger>
            <span className="text-sm font-semibold text-kumo-default">NT東京 管理</span>
          </div>
          <main className="flex-1 p-6">{children}</main>
        </div>
      </div>
    </Sidebar.Provider>
  );
}
